"use client";
import {JSX, ReactNode, useEffect, useRef, useState} from "react";

import {usePathname, useSearchParams} from "next/navigation";

import type {HeaderDropdownItem} from "@/sections/common/header/parts/aditional-menus/header-dropdown";

import {useLockedBody, useMatchMedia} from "@/shared/hooks";
import {useDetectClickOutside} from "@/shared/hooks/dom/use-detect-click-outside";
import {useElementCssHeight} from "@/shared/hooks/dom/use-element-css-height";
import {useTranslation} from "@/shared/i18n";
import {ButtonSimple} from "@/shared/ui/buttons";
import {LogoBase} from "@/shared/ui/media/logos/logo-base";
import {assetPath} from "@/shared/utils/asset-path";

import {AdditionalMenu} from "./parts/aditional-menus/additional-menu";
import {HeaderDropdown} from "./parts/aditional-menus/header-dropdown";

import "./header-base.scss";

export type {HeaderDropdownItem} from "./parts/aditional-menus/header-dropdown";

export interface HeaderLink {
    id: number;
    href: string;
    labelKey: string;
    dropdownItems?: Array<HeaderDropdownItem>;
}

export interface AdditionalLink {
    id: number;
    href: string;
    labelKey: string;
    descriptionKey: string;
    icon?: string;
}

export interface HeaderBaseProps {
    headerLinks: Array<HeaderLink>;
    additionalLinks?: Array<AdditionalLink>;
    children?: ReactNode;
    className?: string;
}

export function HeaderBase({headerLinks, className, additionalLinks = [], children}: HeaderBaseProps): JSX.Element {
    const [isOpen, setIsOpen] = useState(false);
    const {t} = useTranslation();

    const pathname = usePathname();
    const searchParams = useSearchParams();

    const headerRef = useRef<HTMLElement>(null);
    const menuButtonRef = useRef<HTMLButtonElement>(null);

    const toggleMenu = (): void => {
        setIsOpen((prev) => !prev);
    };

    const closeMenu = (): void => {
        setIsOpen(false);
    };

    useElementCssHeight({ref: headerRef, cssVariable: "--header-height"});

    // Close menu when clicking outside (ignore the menu button itself)
    const additionalMenuRef = useDetectClickOutside<HTMLDivElement>({
        onTriggered: closeMenu,
        ignoredRefs: [menuButtonRef],
        disableClick: !isOpen,
        disableTouch: !isOpen,
        disableKeys: !isOpen,
    });

    // Translate header links
    const translatedHeaderLinks = (headerLinks || []).map((link) => ({
        ...link,
        label: t(link.labelKey as Parameters<typeof t>[0]),
    }));

    // Translate additional links
    const translatedAdditionalLinks = (additionalLinks || []).map((link) => ({
        ...link,
        label: t(link.labelKey as Parameters<typeof t>[0]),
        description: t(link.descriptionKey as Parameters<typeof t>[0]),
    }));

    // Close menu on route change
    useEffect(() => {
        if (isOpen) {
            closeMenu();
        }
    }, [pathname, searchParams]);

    const isTable = useMatchMedia({maxWidth: 991});
    useLockedBody({locked: isOpen && isTable});

    return (
        <header ref={headerRef} className={`header_base ${className ?? ""}`} role="banner">
            <div className="header_base__content">
                <LogoBase logoLink="/" imageLink={assetPath("/images/logos/casago-logo.svg")} width={222} height={72} alt="Site logo" />
                {children}
                <div className="header_base__nav_bar">
                    <nav className="header_base__main_nav" aria-label={t("nav.mainNavigation")}>
                        {translatedHeaderLinks.map((link) =>
                            link.dropdownItems && link.dropdownItems.length > 0 ? (
                                <HeaderDropdown key={link.id} label={link.label} href={link.href} items={link.dropdownItems} />
                            ) : (
                                <ButtonSimple as="link" key={link.id} href={link.href} variant="primary">
                                    {link.label}
                                </ButtonSimple>
                            ),
                        )}
                    </nav>

                    <div className="header_base__aside">
                        <button
                            ref={menuButtonRef}
                            type="button"
                            className={`header_base__menu-button ${isOpen ? "is-open" : ""}`}
                            aria-controls="header-additional-menu"
                            aria-expanded={isOpen}
                            aria-label={isOpen ? t("nav.closeMenu") : t("nav.openMenu")}
                            onClick={toggleMenu}
                        >
                            <span />
                            <span />
                            <span />
                        </button>
                    </div>
                </div>

                <div
                    ref={additionalMenuRef}
                    id="header-additional-menu"
                    className={`header_base__additional ${isOpen ? "is-open" : ""}`}
                    role="dialog"
                    aria-hidden={!isOpen}
                >
                    <div className="header_base__additional-inner">
                        <AdditionalMenu headerLinks={translatedHeaderLinks} additionalLinks={translatedAdditionalLinks} onClose={closeMenu} />
                    </div>
                </div>
            </div>
        </header>
    );
}
